;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import type { UseFormReturn } from "react-hook-form";
import { PROVIDER_NAMES } from "@/components/settings/data-providers/constants";
import type { CreateDataProviderSchema } from "@/lib/schema/data-provider";
import type { ProviderType } from "@/types/dataProvider";

export interface StepDefinition {
	id: number;
	title: string;
	description: string;
}

export interface ProviderStepResult {
	success: boolean;
	errorFields?: string[];
	data?: any;
	shouldAdvance?: boolean;
}

export interface ProviderConfig {
	type: ProviderType;
	steps: StepDefinition[];

	/**
	 * Called when the provider form is initialized
	 */
	onInit?: (
		form: UseFormReturn<CreateDataProviderSchema>,
		workspaceId?: string,
	) => void;

	/**
	 * Called when the provider form is reset
	 */
	onReset?: (form: UseFormReturn<CreateDataProviderSchema>) => void;

	/**
	 * Validate and process a step before advancing
	 * Return shouldAdvance: true to move to next step
	 * Return shouldAdvance: false to stay on current step
	 */
	onStepSubmit?: (
		stepIndex: number,
		data: CreateDataProviderSchema,
		helpers: {
			pingProvider: (data: CreateDataProviderSchema) => Promise<any>;
			form: UseFormReturn<CreateDataProviderSchema>;
			workspaceId?: string;
		},
	) => Promise<ProviderStepResult>;

	/**
	 * Called on final submission (last step or single-step providers)
	 */
	onFinalSubmit?: (
		data: CreateDataProviderSchema,
		helpers: {
			createProvider: (data: CreateDataProviderSchema) => Promise<any>;
			form: UseFormReturn<CreateDataProviderSchema>;
			pingProvider: (data: CreateDataProviderSchema) => Promise<any>;
			workspaceId?: string;
		},
	) => Promise<ProviderStepResult>;

	/**
	 * Check if the current step can be submitted
	 */
	canSubmitStep?: (props: {
		stepIndex: number;
		form: UseFormReturn<CreateDataProviderSchema>;
		pingProvider: (data: CreateDataProviderSchema) => Promise<any>;
		workspaceId?: string;
	}) => boolean;

	/**
	 * Get button text for current step
	 */
	getSubmitButtonText?: (
		stepIndex: number,
		totalSteps: number,
		isSubmitting: boolean,
		workspaceId?: string,
	) => string;
}

// Default configuration for single-step providers
export const createSimpleProviderConfig = (
	type: ProviderType,
	errorFields: string[],
): ProviderConfig => ({
	getSubmitButtonText: (_stepIndex, _totalSteps, isSubmitting) => {
		return isSubmitting ? "Saving..." : "Save";
	},
	onFinalSubmit: async (data, { createProvider, form, pingProvider }) => {
		try {
			const pingResult = await pingProvider(data);
			if (!pingResult?.isCredentialsValid) {
				return { success: false };
			}

			await createProvider(data);
			return { success: true };
		} catch (_error) {
			// Set errors on the specified fields
			for (const field of errorFields) {
				form.setError(field as any, {
					message: "This provided credential is possibly incorrect",
				});
			}
			return { errorFields, success: false };
		}
	},
	onInit: (form) => {
		form.reset({
			// @ts-expect-error
			data: {
				credentials: {
					apiKey: "",
					apiToken: "",
					clientId: "",
					clientSecret: "",
					databaseName: "",
					developerId: "",
					email: "",
					password: "",
					personalAccessToken: "",
					superAdminEmail: "",
				},
				type,
			},
			workspaceId: form.getValues("workspaceId"),
		});
	},
	steps: [
		{
			description: `Connect ${PROVIDER_NAMES[type]}`,
			id: 1,
			title: "Connection Credentials",
		},
	],
	type,
});
